import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Toast, Button } from 'react-bootstrap';
import { Card, Form } from 'react-bootstrap';
import InfoProveedorEvaluacion from './InfoProveedorEvaluacion';

const CalificacionesProveedores = ({
  proveedorId,
  proveedorNombre,
  proveedorEmail,
  tipoPersona,
  evaluacionData,
  onEvaluacionCompletada,
  modoLectura = false,
  modoEdicion = false,
  onCancelarEdicion,
  userInfo
}) => {
  const [proveedor, setProveedor] = useState({
    nombre: proveedorNombre || '',
    representante: '',
    materiales: '',
    fecha: new Date().toISOString().split('T')[0]
  });

  const [evaluaciones, setEvaluaciones] = useState({
    seccion1: [0, 0, 0],
    seccion2: [0, 0, 0],
    seccion3: [0, 0, 0],
    seccion4: [0, 0, 0]
  });

  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [observacionesEvaluacion, setObservacionesEvaluacion] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const pesos = [0.25, 0.25, 0.25, 0.25];

  const preguntas = {
    seccion1: [
      "¿El proveedor cumple con los plazos establecidos para la entrega de los productos y/o servicios?",
      "¿El proveedor cumple con la cantidad de los bienes / servicios requeridos?",
      "¿El tiempo de cotización, entrega de requisitos y entrega de los bienes y/o servicios fue el estimado para la compra o contratación?"
    ],
    seccion2: [
      "¿El proveedor cumple con los requisitos y/o especificaciones técnicas establecidas para el bien y/o servicio adquirido?",
      "¿El proveedor presentó incontrolables graves en la calidad y cumplimiento de especificaciones técnicas exigidas, ocasionando incumplimiento del contrato y dando lugar a la aplicación de garantías?",
      "¿El proveedor cumple con la calidad del producto o servicio prestado?"
    ],
    seccion3: [
      "¿El proveedor mantiene precios competitivos acorde al mercado, cantidad y calidad?",
      "¿El proveedor es flexible en base a condiciones de crédito?",
      "¿La facturación es oportuna y correcta?"
    ],
    seccion4: [
      "¿El proveedor presta un buen asesoramiento, atención a solicitudes de ofertas y apoyo técnico para la solución de sus requerimientos?",
      "¿El proveedor presta la gestión oportuna de quejas y reclamos?",
      "¿El proveedor lleva un control postventa sobre la calidad y/o correcta entrega y satisfacción del bien y/o servicio contratado?"
    ]
  };

  const camposEditables = !modoLectura || modoEdicion;

  const cargarDatosEvaluacion = useCallback((data) => {
    if (data && data.items) {
      const nuevasEvaluaciones = {
        seccion1: Array.isArray(data.items.seccion1) && data.items.seccion1.length === 3
          ? [...data.items.seccion1]
          : [0, 0, 0],
        seccion2: Array.isArray(data.items.seccion2) && data.items.seccion2.length === 3
          ? [...data.items.seccion2]
          : [0, 0, 0],
        seccion3: Array.isArray(data.items.seccion3) && data.items.seccion3.length === 3
          ? [...data.items.seccion3]
          : [0, 0, 0],
        seccion4: Array.isArray(data.items.seccion4) && data.items.seccion4.length === 3
          ? [...data.items.seccion4]
          : [0, 0, 0]
      };

      setEvaluaciones(nuevasEvaluaciones);

      if (data.observaciones) {
        setObservacionesEvaluacion(data.observaciones);
      } else {
        setObservacionesEvaluacion('');
      }

      setProveedor(prev => ({
        ...prev,
        nombre: data.proveedor_nombre || proveedorNombre || '',
        representante: data.representante_nombre || '',
        materiales: data.materiales_servicio || '',
        fecha: data.fecha_evaluacion || new Date().toISOString().split('T')[0]
      }));
    } else {
      resetearFormulario();
    }
  }, [proveedorNombre]);

  const resetearFormulario = () => {
    setEvaluaciones({
      seccion1: [0, 0, 0],
      seccion2: [0, 0, 0],
      seccion3: [0, 0, 0],
      seccion4: [0, 0, 0]
    });
    setObservacionesEvaluacion('');
    setProveedor({
      nombre: proveedorNombre || '',
      representante: '',
      materiales: '',
      fecha: new Date().toISOString().split('T')[0]
    });
  };

  useEffect(() => {
    cargarDatosEvaluacion(evaluacionData);
  }, [evaluacionData, cargarDatosEvaluacion]);

  const mostrarToast = (mensaje, tipo = 'success') => {
    setToastMessage(mensaje);
    setToastType(tipo);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const calcularPromedioSeccion = (seccion) => {
    const valores = evaluaciones[seccion];
    const suma = valores.reduce((acc, val) => acc + val, 0);
    return valores.length > 0 ? suma / valores.length : 0;
  };

  const calcularEvaluacionSeccion = (promedio, peso) => {
    return ((promedio * peso) / 5) * 100;
  };

  const calcularCalificacionFinal = () => {
    return [
      calcularEvaluacionSeccion(calcularPromedioSeccion('seccion1'), pesos[0]),
      calcularEvaluacionSeccion(calcularPromedioSeccion('seccion2'), pesos[1]),
      calcularEvaluacionSeccion(calcularPromedioSeccion('seccion3'), pesos[2]),
      calcularEvaluacionSeccion(calcularPromedioSeccion('seccion4'), pesos[3])
    ].reduce((a, b) => a + b, 0);
  };

  const calcularPromedioGeneral = () => {
    const promedios = [
      calcularPromedioSeccion('seccion1'),
      calcularPromedioSeccion('seccion2'),
      calcularPromedioSeccion('seccion3'),
      calcularPromedioSeccion('seccion4')
    ];
    return promedios.reduce((a, b) => a + b, 0) / promedios.length;
  };

  const handleEvaluacionChange = (seccion, index, valor) => {
    if (!camposEditables) return;

    const nuevas = { ...evaluaciones };
    nuevas[seccion][index] = parseInt(valor);
    setEvaluaciones(nuevas);
  };

  const obtenerEstadoCumplimiento = (calificacion) => {
    if (calificacion >= 60) return "CUMPLE";
    if (calificacion >= 46) return "CUMPLE PARCIALMENTE";
    if (calificacion >= 45) return "NO CUMPLE";
    return "NO APLICABLE";
  };

  const guardarEvaluacion = async () => {
    if (!camposEditables || isLoading) return;

    try {
      setIsLoading(true);
      const calificacionFinal = calcularCalificacionFinal();
      const promedioGeneral = calcularPromedioGeneral();
      const estado = obtenerEstadoCumplimiento(calificacionFinal);
      const fechaActual = new Date().toISOString();
      const fechaFormateada = new Date().toISOString().split('T')[0];

      const datos = {
        id_proveedor: proveedorId,
        id_usuario_evaluador: userInfo?.id_usuario,
        fecha_creacion: fechaActual,
        promedio_general: promedioGeneral,
        calificacion_final: calificacionFinal,
        porcentaje_final: calificacionFinal,
        estado_cumplimiento: estado,
        representante_evaluacion: '',
        fecha_firma_evaluacion: fechaFormateada,
        observaciones: observacionesEvaluacion,
        evaluaciones: evaluaciones
      };

      let url, method;
      if (evaluacionData?.id_evaluacion && modoEdicion) {
        url = `https://apiprov.conduto.com.ec/api/evaluaciones-proveedores/${evaluacionData.id_evaluacion}`;
        method = 'PUT';
      } else {
        url = `https://apiprov.conduto.com.ec/api/evaluaciones-proveedores`;
        method = 'POST';
      }

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(datos)
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Error ${res.status}: ${errorText}`);
      }

      const resultado = await res.json();

      mostrarToast(
        evaluacionData && modoEdicion ?
          'Evaluación actualizada correctamente' :
          'Evaluación creada correctamente'
      );

      if (onEvaluacionCompletada) {
        onEvaluacionCompletada({
          ...datos,
          id_evaluacion: resultado.id_evaluacion,
          fecha_creacion: new Date().toISOString(),
          items: evaluaciones
        });
      }

      if (!modoEdicion) {
        resetearFormulario();
      }

    } catch (e) {
      mostrarToast('Error al guardar la evaluación: ' + e.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const renderSeccion = (titulo, seccionKey, preguntasSeccion, idx) => {
    const promedio = calcularPromedioSeccion(seccionKey);
    const evaluacion = calcularEvaluacionSeccion(promedio, pesos[idx]);

    return (
      <div className="card mb-3" key={seccionKey}>
        <div className="card-header py-2" style={{ backgroundColor: '#005793', color: '#fff' }}>
          <h6 className="mb-0" style={{ color: '#fff' }}>{titulo}</h6>
        </div>
        <div className="card-body p-2">
          <div className="table-responsive">
            <table className="table table-bordered table-sm mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Pregunta</th>
                  {[5, 4, 3, 2, 1, 0].map(v => <th key={v}>{v}</th>)}
                  <th className="bg-light">VAL</th>
                  <th className="bg-light">EVAL</th>
                </tr>
              </thead>
              <tbody>
                {preguntasSeccion.map((p, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td className="small">{p}</td>
                    {[5, 4, 3, 2, 1, 0].map(v => (
                      <td key={v}>
                        <input
                          type="radio"
                          name={`${seccionKey}-${i}`}
                          value={v}
                          checked={evaluaciones[seccionKey][i] === v}
                          onChange={e => handleEvaluacionChange(seccionKey, i, e.target.value)}
                          className="form-check-input"
                          disabled={!camposEditables}
                          style={!camposEditables ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                        />
                      </td>
                    ))}
                    <td className="bg-light text-center">{i === 0 ? promedio.toFixed(2) : ''}</td>
                    <td className="bg-light text-center">{i === 0 ? evaluacion.toFixed(2) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const calificacionFinal = calcularCalificacionFinal();
  const promedioGeneral = calcularPromedioGeneral();

  return (
    <div className="container-fluid p-0" style={{ minHeight: '400px' }}>
      <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 9999 }}>
        <Toast show={showToast} onClose={() => setShowToast(false)} delay={3000} autohide bg={toastType === 'error' ? 'danger' : 'success'}>
          <Toast.Body className="text-white d-flex align-items-center">
            {toastType !== 'error' && (
              <i className="bi bi-check-circle-fill me-2" style={{ fontSize: 18 }}></i>
            )}
            {toastMessage}
          </Toast.Body>
        </Toast>
      </div>

      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      {modoEdicion && (
        <Alert variant="warning" className="mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Modo de edición:</strong> Estás editando una evaluación existente.
        </Alert>
      )}

      {console.log('Renderizando CalificacionesProveedores con evaluacionData:', evaluacionData)}

      <InfoProveedorEvaluacion
        proveedorId={proveedorId}
        fechaEvaluacion={evaluacionData?.fecha_actualizacion}
      />

      {renderSeccion("SECCIÓN 1: ENTREGA", "seccion1", preguntas.seccion1, 0)}
      {renderSeccion("SECCIÓN 2: CALIDAD", "seccion2", preguntas.seccion2, 1)}
      {renderSeccion("SECCIÓN 3: GESTIÓN COMERCIAL", "seccion3", preguntas.seccion3, 2)}
      {renderSeccion("SECCIÓN 4: SOLUCIÓN DE PROBLEMAS", "seccion4", preguntas.seccion4, 3)}

      <div className="card mt-3">
        <div className="card-header bg-success text-white">
          <h6>TOTALES</h6>
        </div>
        <div className="card-body">
          <table className="table table-bordered table-sm">
            <thead>
              <tr>
                <th>Sección</th>
                <th>Valoración</th>
                <th>Evaluación</th>
              </tr>
            </thead>
            <tbody>
              {['seccion1', 'seccion2', 'seccion3', 'seccion4'].map((s, i) => (
                <tr key={s}>
                  <td>Sección {i + 1}</td>
                  <td className="text-center">{calcularPromedioSeccion(s).toFixed(2)}</td>
                  <td className="text-center">{calcularEvaluacionSeccion(calcularPromedioSeccion(s), pesos[i]).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="table-primary">
              <tr>
                <td className="fw-bold">PROMEDIO GENERAL</td>
                <td className="text-center fw-bold">{promedioGeneral.toFixed(2)}</td>
                <td className="text-center fw-bold">{calificacionFinal.toFixed(2)}</td>
              </tr>
              <tr>
                <td colSpan={2} className="text-end fw-bold">ESTADO:</td>
                <td className="text-center fw-bold">{obtenerEstadoCumplimiento(calificacionFinal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <Card className="mt-3">
        <Card.Header className="bg-light">
          <h6 className="mb-0">Observaciones de la Evaluación</h6>
        </Card.Header>
        <Card.Body>
          <Form.Group>
            <Form.Label><strong>Observaciones específicas de la evaluación:</strong></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={observacionesEvaluacion}
              onChange={(e) => camposEditables && setObservacionesEvaluacion(e.target.value)}
              placeholder="Ingrese observaciones específicas para la evaluación del desempeño del proveedor"
              disabled={!camposEditables}
            />
          </Form.Group>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-end mt-3 pb-3 gap-2">
        {modoEdicion && (
          <>
            <Button variant="secondary" size="sm" onClick={onCancelarEdicion} disabled={isLoading}>
              <i className="bi bi-x-circle me-1"></i> Cancelar
            </Button>
            <Button variant="primary" size="sm" onClick={guardarEvaluacion} disabled={isLoading}>
              <i className="bi bi-save me-1"></i>
              {isLoading ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </>
        )}
        {!evaluacionData && !modoEdicion && (
          <Button variant="primary" size="sm" onClick={guardarEvaluacion} disabled={isLoading}>
            <i className="bi bi-save me-1"></i>
            {isLoading ? 'Guardando...' : 'Guardar Evaluación'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CalificacionesProveedores;